// Components & Hooks
import { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "@components/buttons/button";
import { useNavigate } from "react-router-dom";
import { ZuAuthStore } from "@hooks/stores/zustand-auth-store";
import { CustomerTypeEnum, CustomerTypeTypes } from "@hooks/dtos/user-dto";

// ── Types ─────────────────────────────────────────────────────────────────────
export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQSection {
  id: string;
  title: string;
  icon: string;
  audience: "cliente" | "consultor" | "gerente" | "geral";
  type: CustomerTypeTypes[];
  items: FAQItem[];
}

export const FAQ_DATA: FAQSection[] = [
  // ── GERAL ────────────────────────────────────────────────────────────────
  {
    id: "geral-rastreio",
    title: "Rastreamento e Busca",
    icon: "bi-search",
    audience: "geral",
    type: [],
    items: [
      {
        question: "Como rastrear meu pedido sem fazer login?",
        answer:
          "Acesse a página inicial, insira o número do pedido ou o código de rastreio no campo de busca e clique em 'Buscar'. Os estágios de entrega serão exibidos sem a necessidade de conta. Nenhum dado pessoal ou sensível é exibido nessa modalidade.",
      },
      {
        question: "Quais informações aparecem na busca sem login?",
        answer:
          "São exibidos apenas os estágios do pedido (ex.: em preparação, enviado, em trânsito, entregue) e a data de cada atualização. Dados como CPF, endereço completo e invoice de compra não ficam visíveis sem autenticação.",
      },
      // {
      //   question: "Com que frequência os estágios de entrega são atualizados?",
      //   answer:
      //     "As atualizações são registradas manualmente pela equipe conforme o pedido avança no processo logístico. O status exibido sempre reflete a última atualização inserida.",
      // },
      {
        question: "O que fazer se o código de rastreio não for encontrado?",
        answer:
          "Verifique se o código foi digitado corretamente, sem espaços ou caracteres extras. Caso o problema persista, entre em contato com o suporte ou com seu consultor, pois o rastreio pode ainda não ter sido vinculado ao pedido.",
      },
    ],
  },

  // ── CLIENTE ──────────────────────────────────────────────────────────────
  {
    id: "cliente-acesso",
    title: "Acesso e Primeiro Login — Clientes",
    icon: "bi-person",
    audience: "cliente",
    type: [],
    items: [
      {
        question: "Como faço meu primeiro acesso à plataforma?",
        answer:
          "No primeiro acesso, utilize seu CPF como login e também como senha (somente números, sem pontos ou traços). Após entrar, o sistema solicitará a criação de uma nova senha. Essa troca é obrigatória e ocorre uma única vez.",
      },
      // {
      //   question: "Esqueci minha senha. Como recupero o acesso?",
      //   answer:
      //     "Na tela de login, clique em 'Esqueci minha senha' e informe o e-mail cadastrado. Um link de redefinição será enviado. Caso não receba, verifique a pasta de spam ou entre em contato com o suporte.",
      // },
      {
        question: "Meus pedidos antigos aparecem na plataforma?",
        answer:
          "Sim. Todos os pedidos registrados no seu CPF desde 2024 estão disponíveis na listagem, independentemente do status atual.",
      },
    ],
  },
  {
    id: "cliente-pedidos",
    title: "Pedidos e Histórico — Clientes",
    icon: "bi-box-seam",
    audience: "cliente",
    type: [],
    items: [
      {
        question: "Como acesso meus pedidos logado?",
        answer:
          "Após o login, a listagem completa de todos os pedidos vinculados ao seu CPF é exibida automaticamente. Você pode filtrar por data e por status de entrega para localizar um pedido específico.",
      },
      {
        question: "Posso ver a invoice (nota de compra) do meu pedido?",
        answer:
          "Sim, desde que o pedido tenha sido realizado a partir de 18/03/2026. Pedidos anteriores a essa data não possuem invoice disponível na plataforma. Para acessar, basta clicar no pedido desejado enquanto estiver logado.",
      },
      {
        question: "Como exporto minha lista de pedidos?",
        answer:
          "Na tela de listagem, utilize o botão 'Exportar para Excel'. O arquivo gerado contém todos os pedidos do filtro aplicado no momento da exportação.",
      },
      // {
      //   question:
      //     "Por que um pedido aparece sem atualização de status há vários dias?",
      //   answer:
      //     "Os estágios são atualizados manualmente conforme a movimentação logística. Em caso de dúvida sobre um pedido parado, entre em contato com seu consultor ou com o suporte.",
      // },
    ],
  },

  // ── CONSULTOR ────────────────────────────────────────────────────────────
  {
    id: "consultor-acesso",
    title: "Acesso e Funcionalidades — Consultores",
    icon: "bi-headset",
    audience: "consultor",
    type: [CustomerTypeEnum.MANAGER, CustomerTypeEnum.SALESPERSON],
    items: [
      {
        question: "Como o consultor acessa a plataforma?",
        answer:
          "Consultores possuem credenciais próprias fornecidas pela equipe de gestão. O acesso é feito na mesma tela de login dos clientes, porém o painel exibido é diferente, mostrando a lista de clientes vinculados ao consultor.",
      },
      {
        question: "Consigo ver os pedidos de todos os meus clientes?",
        answer:
          "Sim. Após o login, você tem acesso à listagem de pedidos de todos os clientes vinculados à sua carteira. É possível filtrar pelo nome do cliente para localizar rapidamente um pedido específico.",
      },
      {
        question: "Tenho acesso às invoices dos clientes?",
        answer:
          "Sim, para pedidos a partir de 18/03/2026. O acesso segue a mesma regra aplicada aos clientes: a invoice fica disponível ao abrir o detalhe do pedido enquanto autenticado.",
      },
      {
        question: "Posso exportar a lista de pedidos dos meus clientes?",
        answer:
          "Sim. A exportação para Excel está disponível no painel do consultor e considera os filtros aplicados no momento, incluindo o filtro por nome de cliente.",
      },
      {
        question:
          "Qual a frequência de atualização dos dados e pedidos na plataforma?",
        answer:
          "A atualização depende da equipe interna, que insere as informações manualmente no Zoho. Assim que registradas, as informações são sincronizadas automaticamente com nossa plataforma em questão de segundos, mantendo a data original do evento (ex: se o pedido foi atualizado na transportadora no sábado, ao ser inserido no sábado já entrará com a data correta de sábado).",
      },
      {
        question: "Em que momento o pedido aparece na plataforma?",
        answer:
          "Apenas após a confirmação do pagamento pelo BackOffice. O pedido precisa estar criado, registrado e com pagamento aprovado (PIX confirmado, TED compensado, etc.). Pedidos sem pagamento aprovado não são exibidos.",
      },
    ],
  },

  // ── GERENTE ──────────────────────────────────────────────────────────────
  {
    id: "gerente-acesso",
    title: "Acesso e Visão Geral — Gerentes",
    icon: "bi-diagram-3",
    audience: "gerente",
    type: [CustomerTypeEnum.MANAGER],
    items: [
      {
        question: "O que o gerente consegue visualizar na plataforma?",
        answer:
          "O gerente tem acesso ao painel completo da sua equipe: é possível ver a lista de consultores vinculados e, dentro de cada consultor, os pedidos dos respectivos clientes.",
      },
      {
        question: "Como o gerente acessa a plataforma?",
        answer:
          "O acesso é feito na mesma tela de login, com credenciais de gerente fornecidas pela administração. O sistema identifica o perfil automaticamente e exibe o painel correspondente.",
      },
      {
        question: "O gerente pode exportar dados?",
        answer:
          "Sim. A exportação para Excel está disponível no painel gerencial e pode ser aplicada com os filtros de consultor e período ativos.",
      },
    ],
  },
];

export const PLACEHOLDER_EXAMPLES = [
  "número do pedido",
  "código de rastreio",
  "primeiro acesso",
  "invoice",
  "exportar Excel",
  "consultor",
  "filtrar por status",
  "senha",
  "histórico de pedidos",
];

// ── Animated Placeholder Hook ─────────────────────────────────────────────────
function useAnimatedPlaceholder(examples: string[], prefix = "Ex: ") {
  const [placeholder, setPlaceholder] = useState(prefix);
  const exIndex = useRef(0);
  const charIndex = useRef(0);
  const erasing = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const tick = () => {
      const word = examples[exIndex.current];

      if (!erasing.current) {
        charIndex.current++;
        setPlaceholder(prefix + word.slice(0, charIndex.current));
        if (charIndex.current === word.length) {
          erasing.current = true;
          timer.current = setTimeout(tick, 1600);
        } else {
          timer.current = setTimeout(tick, 80);
        }
      } else {
        charIndex.current--;
        setPlaceholder(prefix + word.slice(0, charIndex.current));
        if (charIndex.current === 0) {
          erasing.current = false;
          exIndex.current = (exIndex.current + 1) % examples.length;
          timer.current = setTimeout(tick, 400);
        } else {
          timer.current = setTimeout(tick, 45);
        }
      }
    };

    timer.current = setTimeout(tick, 1000);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return placeholder;
}

// ── FAQ Item ──────────────────────────────────────────────────────────────────
function FAQAccordionItem({
  item,
  id,
  parentId,
}: {
  item: FAQItem;
  id: string;
  parentId: string;
}) {
  return (
    <div className="accordion-item faq-item border-0 mb-2">
      <h2 className="accordion-header">
        <button
          className="accordion-button faq-btn collapsed"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target={`#${id}`}
          aria-expanded="false"
          aria-controls={id}
        >
          {item.question}
        </button>
      </h2>
      <div
        id={id}
        className="accordion-collapse collapse"
        data-bs-parent={`#${parentId}`}
      >
        <div className="accordion-body faq-answer">{item.answer}</div>
      </div>
    </div>
  );
}

// ── FAQ Section ───────────────────────────────────────────────────────────────
function FAQSection({ section }: { section: FAQSection }) {
  const accordionId = `accordion-${section.id}`;
  return (
    <div className="faq-section mb-4">
      <div className="faq-section-header d-flex align-items-center gap-2 mb-3">
        <span className="faq-section-icon">
          <i className={`bi ${section.icon}`}></i>
        </span>
        <h5 className="mb-0 faq-section-title">{section.title}</h5>
      </div>
      <div className="accordion faq-accordion" id={accordionId}>
        {section.items.map((item, idx) => (
          <FAQAccordionItem
            key={idx}
            item={item}
            id={`${accordionId}-item-${idx}`}
            parentId={accordionId}
          />
        ))}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export function FAQ() {
  const { customer } = ZuAuthStore();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const placeholder = useAnimatedPlaceholder(PLACEHOLDER_EXAMPLES);

  const filtered = useMemo(() => {
    if (!query.trim()) return FAQ_DATA;
    const q = query.toLowerCase();
    return FAQ_DATA.map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          item.question.toLowerCase().includes(q) ||
          item.answer.toLowerCase().includes(q),
      ),
    })).filter((s) => s.items.length > 0);
  }, [query]);

  const totalResults = filtered.reduce((acc, s) => acc + s.items.length, 0);

  return (
    <>
      {/* ── Inline Styles ─────────────────────────────────────────────────── */}
      <style>{`
        :root {
          --faq-teal:        #0e9f9f;
          --faq-teal-light:  #b9eaed;
          --faq-teal-xlight: rgba(14,159,159,0.08);
          --faq-navy:        #0f172a;
          --faq-muted:       #475569;
          --faq-border:      #e2e8f0;
          --faq-bg:          #f0fafa;
          --faq-white:       #ffffff;
          --faq-radius:      12px;
        }

        /* Page background */
        .faq-page-wrapper {
          background: var(--faq-bg);
          min-height: 60vh;
          padding: clamp(2rem, 4vw, 3.5rem) 0;
        }

        /* Hero card */
        .faq-hero {
          background: var(--faq-white);
          border-radius: var(--faq-radius);
          border: 1px solid var(--faq-border);
          padding: 1.75rem 2rem;
          display: flex;
          align-items: center;
          gap: 1.25rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 1px 4px rgba(0,0,0,0.05);
        }
        .faq-hero-icon {
          width: 52px;
          height: 52px;
          border-radius: 14px;
          background: var(--faq-teal-xlight);
          color: var(--faq-teal);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          flex-shrink: 0;
        }
        .faq-hero h2 { color: var(--faq-navy); font-size: clamp(1.3rem, 2.5vw, 1.75rem); font-weight: 700; margin-bottom: .2rem; }
        .faq-hero p  { color: var(--faq-muted); font-size: .97rem; margin: 0; }

        /* Search card */
        .faq-search-card {
          background: var(--faq-white);
          border: 1.5px solid var(--faq-teal-light);
          border-radius: var(--faq-radius);
          padding: 1.5rem 1.75rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 1px 6px rgba(14,159,159,0.07);
        }
        .faq-search-card label { font-weight: 600; color: var(--faq-navy); font-size: .95rem; }
        .faq-search-card .sub  { color: var(--faq-muted); font-size: .85rem; margin-bottom: .75rem; }

        .faq-search-wrap { position: relative; }
        .faq-search-wrap .bi-search {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--faq-teal);
          font-size: 1rem;
          pointer-events: none;
        }
        .faq-input {
          width: 100%;
          padding: .65rem 1rem .65rem 2.6rem;
          border: 1.5px solid var(--faq-border);
          border-radius: 8px;
          font-size: .97rem;
          background: #f8fdfd;
          color: var(--faq-navy);
          outline: none;
          transition: border-color .2s, box-shadow .2s;
        }
        .faq-input:focus {
          border-color: var(--faq-teal);
          box-shadow: 0 0 0 3px rgba(14,159,159,0.15);
          background: #fff;
        }

        /* No results */
        .faq-empty {
          text-align: center;
          padding: 2.5rem 1rem;
          color: var(--faq-muted);
        }
        .faq-empty i { font-size: 2rem; color: var(--faq-teal-light); display: block; margin-bottom: .5rem; }

        /* Section */
        .faq-section-header { margin-bottom: .75rem; }
        .faq-section-icon {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          background: var(--faq-teal-xlight);
          color: var(--faq-teal);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          flex-shrink: 0;
        }
        .faq-section-title { color: var(--faq-navy); font-size: 1rem; font-weight: 600; }

        /* Accordion */
        .faq-item {
          border-radius: 8px !important;
          overflow: hidden;
        }
        .faq-btn {
          background: var(--faq-white) !important;
          color: var(--faq-navy) !important;
          font-size: .95rem;
          font-weight: 500;
          border: 1px solid var(--faq-border) !important;
          border-radius: 8px !important;
          padding: .85rem 1.1rem;
          box-shadow: none !important;
          transition: background .15s;
        }
        .faq-btn:not(.collapsed) {
          background: var(--faq-teal-xlight) !important;
          color: var(--faq-teal) !important;
          border-color: var(--faq-teal-light) !important;
          border-bottom-left-radius: 0 !important;
          border-bottom-right-radius: 0 !important;
        }
        .faq-btn::after {
          filter: none !important;
          background-image: none !important;
          content: '+' !important;
          font-size: 1.25rem;
          font-weight: 300;
          color: var(--faq-teal);
          width: auto;
          height: auto;
          transform: none !important;
        }
        .faq-btn:not(.collapsed)::after { content: '−' !important; }

        .accordion-collapse .accordion-body.faq-answer {
          background: #f8fdfd;
          color: var(--faq-muted);
          font-size: .93rem;
          line-height: 1.65;
          border: 1px solid var(--faq-teal-light);
          border-top: none;
          border-bottom-left-radius: 8px;
          border-bottom-right-radius: 8px;
          padding: 1rem 1.1rem;
        }

        /* Sections wrapper card */
        .faq-body-card {
          background: var(--faq-white);
          border: 1px solid var(--faq-border);
          border-radius: var(--faq-radius);
          padding: 1.75rem 1.75rem;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04);
        }

        /* CTA section */
        .faq-cta {
          background: linear-gradient(135deg, rgba(127,211,216,0.25), rgba(185,234,237,0.35));
          padding: clamp(3rem, 6vw, 6rem);
        }
      `}</style>

      {/* Bootstrap Icons CDN (injected once) */}
      {!document.getElementById("bi-cdn") &&
        (() => {
          const link = document.createElement("link");
          link.id = "bi-cdn";
          link.rel = "stylesheet";
          link.href =
            "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css";
          document.head.appendChild(link);
          return null;
        })()}

      <div className="faq-page-wrapper">
        <div className="container" style={{ maxWidth: 960 }}>
          {/* Hero */}
          <div className="faq-hero">
            <div className="faq-hero-icon">
              <i className="bi bi-question-circle"></i>
            </div>
            <div>
              <h2>FAQ e Ajuda</h2>
              <p>
                Acompanhe suas encomendas com muita praticidade e total
                transparência.
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="faq-search-card">
            <label htmlFor="faq-search">Pesquisar na FAQ</label>
            <p className="sub">
              Digite uma palavra-chave para filtrar as perguntas em tempo real.
            </p>
            <div className="faq-search-wrap">
              <i className="bi bi-search"></i>
              <input
                id="faq-search"
                type="text"
                className="faq-input"
                placeholder={placeholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>

          {/* Results */}
          <div className="faq-body-card">
            {filtered.length === 0 ? (
              <div className="faq-empty">
                <i className="bi bi-search"></i>
                <p className="mb-0">
                  Nenhuma pergunta encontrada para <strong>"{query}"</strong>.
                </p>
              </div>
            ) : (
              <>
                {query.trim() && (
                  <p className="text-muted mb-3" style={{ fontSize: ".88rem" }}>
                    <strong>{totalResults}</strong> resultado
                    {totalResults !== 1 ? "s" : ""} para "{query}"
                  </p>
                )}

                {filtered.map((section) => {
                  if (
                    section.type.length > 0 &&
                    (!customer ||
                      !section.type.includes(customer.customer.type))
                  ) {
                    return;
                  }

                  return <FAQSection key={section.id} section={section} />;
                })}
              </>
            )}
          </div>
        </div>
      </div>

      {/* CTA SECTION */}
      <section className="container-fluid d-flex justify-content-center align-items-center text-center faq-cta">
        <div className="col-12 col-lg-8">
          <h4
            className="fw-bold mb-3"
            style={{
              color: "#0f172a",
              fontSize: "clamp(1.2rem, 2.4vw, 1.75rem)",
            }}
          >
            Não restam mais dúvidas?
          </h4>
          <h2
            className="fw-bold mb-3"
            style={{ color: "#0f172a", fontSize: "clamp(1.5rem, 3vw, 2.2rem)" }}
          >
            Pronto para acompanhar os seus pedidos?
          </h2>
          <p
            className="mb-4"
            style={{
              fontSize: "1.05rem",
              color: "#475569",
              maxWidth: "620px",
              margin: "0 auto",
            }}
          >
            Insira seu código de rastreio e tenha total controle da sua entrega.
          </p>
          <Button
            className="btn btn-info text-white btn-lg px-5 py-3 rounded-pill shadow-sm"
            onClick={() => {
              navigate("/");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <span className="fw-bold">Buscar agora</span>
          </Button>
        </div>
      </section>
    </>
  );
}
